import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/admin/ResourceTable";
import { X, Loader2, Search } from "lucide-react";

const TONE = { active: "green", used: "blue", blocked: "red", expired: "amber" };
const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function BatchDetail({ batch, initialStatus = "", onClose }) {
  const [coupons, setCoupons] = useState(null);
  const [status, setStatus] = useState(initialStatus);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/coupon-batches/${batch.id}/export`).then((r) => setCoupons(r.data.coupons || [])).catch((e) => toast.error(apiErr(e)));
  }, [batch.id]);

  const counts = useMemo(() => (coupons || []).reduce((a, c) => ({ ...a, [c.status]: (a[c.status] || 0) + 1 }), {}), [coupons]);
  const list = (coupons || []).filter((c) => (!status || c.status === status) && (!q || c.code.includes(q.toUpperCase())));
  const tabs = [["", "All", coupons?.length || 0], ["used", "Scanned", counts.used || 0], ["active", "Active", counts.active || 0], ["blocked", "Blocked", counts.blocked || 0]];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="batch-detail-modal" className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold text-gray-900">{batch.name}</h3>
            <p className="text-xs text-gray-500">{batch.product_name || "No product"} · {batch.reward_points} pts per QR · created {dt(batch.created_at)}</p>
          </div>
          <button data-testid="batch-detail-close" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-gray-100">
          {tabs.map(([k, l, n]) => (
            <button key={k || "all"} data-testid={`batch-detail-tab-${k || "all"}`} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status === k ? "bg-[#8B0000] text-white" : "bg-gray-100 text-gray-600"}`}>{l} · {n}</button>
          ))}
          <div className="relative ml-auto">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
            <input data-testid="batch-detail-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find code" className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs outline-none focus:border-[#8B0000]" />
          </div>
        </div>
        <div className="overflow-y-auto">
          {!coupons ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div> : (
            <table className="w-full text-sm text-left">
              <thead><tr className="bg-gray-50 sticky top-0">{["Coupon code", "Status", "Scanned by", "Scanned at"].map((h) => <th key={h} className="py-2.5 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">{h}</th>)}</tr></thead>
              <tbody>
                {list.slice(0, 1000).map((c) => (
                  <tr key={c.code} className="border-b border-gray-100" data-testid={`batch-detail-row-${c.code}`}>
                    <td className="py-2 px-4 font-mono text-xs">{c.code}</td>
                    <td className="py-2 px-4"><Badge tone={TONE[c.status]}>{c.status === "used" ? "scanned" : c.status}</Badge></td>
                    <td className="py-2 px-4 font-mono text-xs">{c.used_by_mobile || "—"}</td>
                    <td className="py-2 px-4 text-xs text-gray-500">{dt(c.used_at)}</td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-gray-400">No coupons</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
